import XLSX from 'sheetjs-style'

type ResponseReportRow = {
  'Unique ID'?: string
  Email?: string
  Name?: string
  Section?: string
  'Question No'?: number | string
  'Q ID'?: string
  Response?: string
  'Correct response'?: string
  Marks?: string
  'Time Stamp'?: string
}

export type PaperQuestionKey = {
  id: string
  number: number
  option?: string
  mark?: number
  questionType?: string
}

export type QuestionLookup = {
  id: string
  question_id: string | number
}

const METADATA_COLUMNS = [
  'CandidateId',
  'Name',
  'ApplicationId',
  'CandidateImage',
  'SktId',
  'is_attended',
  'is_disqualified',
  'school_id',
] as const

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E']

const escapeCsvField = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

const isResponseReportRow = (row: ResponseReportRow): boolean => {
  return 'Unique ID' in row && 'Question No' in row && 'Response' in row
}

const isEvaluationCsvFormat = (firstLine: string): boolean => {
  const normalized = firstLine.toLowerCase()
  return normalized.includes('candidateid') && normalized.includes('applicationid')
}

const isResponseReportCsvFormat = (firstLine: string): boolean => {
  const normalized = firstLine.toLowerCase()
  return normalized.includes('unique id') && normalized.includes('question no') && normalized.includes('response')
}

const findResponseReportSheet = (workbook: XLSX.WorkBook): string => {
  const match = workbook.SheetNames.find(name => name.toLowerCase().includes('response'))
  return match || workbook.SheetNames[0]
}

const normalizeResponse = (value: string): string => {
  const cleaned = String(value || '')
    .replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '')
    .trim()
    .toUpperCase()

  if (!cleaned) return ''
  if (OPTION_LABELS.includes(cleaned)) return cleaned

  const numeric = Number(cleaned)
  if (Number.isInteger(numeric) && OPTION_LABELS[numeric]) {
    return OPTION_LABELS[numeric]
  }

  return cleaned.charAt(0)
}

const buildQuestionIdToPaperNumberMap = (
  paperQuestionKeys: PaperQuestionKey[],
  questionLookups: QuestionLookup[]
): Map<string, number> => {
  const uuidToLookupId = new Map<string, string>()
  questionLookups.forEach(item => {
    if (item?.id && item?.question_id != null && item.question_id !== '') {
      uuidToLookupId.set(item.id, String(item.question_id).trim())
    }
  })

  const qIdToPaperNumber = new Map<string, number>()
  paperQuestionKeys.forEach(key => {
    const lookupId = uuidToLookupId.get(key.id)
    if (lookupId && key.number > 0) {
      qIdToPaperNumber.set(lookupId, key.number)
    }
  })

  return qIdToPaperNumber
}

const resolvePaperQuestionNumber = (
  row: ResponseReportRow,
  qIdToPaperNumber: Map<string, number>
): number | null => {
  const qId = String(row['Q ID'] || '').trim()
  if (qId && qIdToPaperNumber.has(qId)) {
    return qIdToPaperNumber.get(qId) || null
  }

  const questionNo = Number(row['Question No'])
  if (!Number.isNaN(questionNo) && questionNo > 0) {
    return questionNo
  }

  return null
}

const getQuestionNumbers = (
  paperQuestionKeys: PaperQuestionKey[] | undefined,
  candidatesMap: Map<string, { name: string; email: string; answers: Record<number, string> }>
): number[] => {
  if (paperQuestionKeys?.length) {
    return Array.from(new Set(paperQuestionKeys.map(key => Number(key.number)).filter(num => num > 0))).sort(
      (a, b) => a - b
    )
  }

  const questionNumbers: number[] = []
  Array.from(candidatesMap.values()).forEach(candidate => {
    Object.keys(candidate.answers).forEach(key => {
      const questionNo = Number(key)
      if (!Number.isNaN(questionNo) && questionNo > 0 && questionNumbers.indexOf(questionNo) === -1) {
        questionNumbers.push(questionNo)
      }
    })
  })

  return questionNumbers.sort((a, b) => a - b)
}

const assertPaperContext = (paperQuestionKeys?: PaperQuestionKey[], questionLookups?: QuestionLookup[]) => {
  if (!paperQuestionKeys?.length) {
    throw new Error(
      'Paper details are required to convert a Response Log file. Please upload from the evaluation request linked to the paper.'
    )
  }

  if (!questionLookups?.length) {
    throw new Error('Could not load question mapping for this paper. Please refresh and try again.')
  }

  const qIdToPaperNumber = buildQuestionIdToPaperNumberMap(paperQuestionKeys, questionLookups)
  if (!qIdToPaperNumber.size) {
    throw new Error(
      'No matching questions found between the Response Log and the selected paper. Please verify the correct paper is linked to this evaluation request.'
    )
  }
}

export const normalizeEvaluationUploadFile = async (
  file: File,
  paperQuestionKeys?: PaperQuestionKey[],
  questionLookups?: QuestionLookup[]
): Promise<{ csv: string; converted: boolean; rowCount?: number; questionCount?: number }> => {
  const fileName = file.name.toLowerCase()
  const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls')

  if (isExcel) {
    assertPaperContext(paperQuestionKeys, questionLookups)
    const fileBuffer = await file.arrayBuffer()
    const { csv, rowCount, questionCount } = convertResponseReportToEvaluationCsv(
      fileBuffer,
      paperQuestionKeys,
      questionLookups
    )
    return { csv, converted: true, rowCount, questionCount }
  }

  const text = await file.text()
  const firstLine = text.split(/\r?\n/)[0] || ''

  if (isEvaluationCsvFormat(firstLine)) {
    return { csv: text, converted: false }
  }

  if (isResponseReportCsvFormat(firstLine)) {
    assertPaperContext(paperQuestionKeys, questionLookups)
    const fileBuffer = await file.arrayBuffer()
    const { csv, rowCount, questionCount } = convertResponseReportToEvaluationCsv(
      fileBuffer,
      paperQuestionKeys,
      questionLookups
    )
    return { csv, converted: true, rowCount, questionCount }
  }

  throw new Error(
    'Invalid format. Upload either the evaluation import CSV or an exam Response Log (.csv, .xlsx, .xls).'
  )
}

export const convertResponseReportToEvaluationCsv = (
  fileBuffer: ArrayBuffer,
  paperQuestionKeys?: PaperQuestionKey[],
  questionLookups?: QuestionLookup[]
): { csv: string; rowCount: number; questionCount: number } => {
  const workbook = XLSX.read(fileBuffer, { type: 'array' })
  const sheetName = findResponseReportSheet(workbook)

  if (!sheetName) {
    throw new Error('The uploaded file has no worksheets.')
  }

  const rows = XLSX.utils.sheet_to_json<ResponseReportRow>(workbook.Sheets[sheetName], { defval: '' })

  if (!rows.length) {
    throw new Error('The uploaded file has no data rows.')
  }

  if (!isResponseReportRow(rows[0])) {
    throw new Error(
      'Invalid format. Expected a Response Report with columns: Unique ID, Email, Name, Question No, Response, etc.'
    )
  }

  const qIdToPaperNumber =
    paperQuestionKeys?.length && questionLookups?.length
      ? buildQuestionIdToPaperNumberMap(paperQuestionKeys, questionLookups)
      : new Map<string, number>()

  const candidatesMap = new Map<string, { name: string; email: string; answers: Record<number, string> }>()

  for (const row of rows) {
    const uniqueId = String(row['Unique ID'] || '').trim()
    if (!uniqueId) continue

    if (!candidatesMap.has(uniqueId)) {
      candidatesMap.set(uniqueId, {
        name: String(row.Name || '').trim(),
        email: String(row.Email || '').trim(),
        answers: {},
      })
    }

    const candidate = candidatesMap.get(uniqueId)!
    const paperQuestionNumber = resolvePaperQuestionNumber(row, qIdToPaperNumber)

    if (paperQuestionNumber) {
      candidate.answers[paperQuestionNumber] = normalizeResponse(String(row.Response || ''))
    }
  }

  if (!candidatesMap.size) {
    throw new Error('No candidate rows found in the uploaded file.')
  }

  const questionNumbers = getQuestionNumbers(paperQuestionKeys, candidatesMap)

  if (!questionNumbers.length) {
    throw new Error('No question responses found in the uploaded file.')
  }

  const headers = [...METADATA_COLUMNS, ...questionNumbers.map(String)]
  const csvRows: string[][] = [headers]

  candidatesMap.forEach((candidate, uniqueId) => {
    csvRows.push([
      uniqueId,
      candidate.name,
      candidate.email || uniqueId,
      '',
      '',
      'Present',
      'No',
      '',
      ...questionNumbers.map(questionNo => candidate.answers[questionNo] ?? ''),
    ])
  })

  const csv = csvRows.map(row => row.map(escapeCsvField).join(',')).join('\n')

  return {
    csv,
    rowCount: candidatesMap.size,
    questionCount: questionNumbers.length,
  }
}

export const downloadCsvFile = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(link.href)
}
